<template>
  <Card tag="form" class="page-mutate-publishing" @submit.prevent="$emit('submit')">
    <template #default>
      <div class="page-mutate-publishing__input">
        <InputComponent
          :value="from"
          :label="labels.from"
          data-field="from"
          :disabled="item.demo || Boolean(getDisabledField('publishing.from'))"
          v-tooltip.bottom="getDisabledField('publishing.from')"
        >
          <InputDate v-model="from" :max="to" :disabled="item.demo || Boolean(getDisabledField('publishing.from'))" />
        </InputComponent>
        <InputComponent
          :value="to"
          :label="labels.to"
          data-field="to"
          :disabled="item.demo || Boolean(getDisabledField('publishing.to'))"
          v-tooltip.bottom="getDisabledField('publishing.to')"
        >
          <InputDate v-model="to" :min="from" :disabled="item.demo || Boolean(getDisabledField('publishing.to'))" />
        </InputComponent>
        <InputComponent
          v-model.lazy="domain"
          :label="labels['publishing.domain']"
          prefix="https://"
          data-field="domain"
          v-bind="domainProps"
          :disabled="item.demo || Boolean(getDisabledField('publishing.domain'))"
          v-tooltip.bottom="getDisabledField('publishing.domain')"
        >
          <template #below>
            <DNS v-if="showDns" ref="dns" :domain="$path('publishing.domain', value)" class="mt-2" />
          </template>
        </InputComponent>
      </div>

      <CustomFields
        class="mt-2"
        v-model="value.customFields"
        :schema="customFieldSchemas"
        :data="value"
        @remove="$ev => removeCustomField($ev)"
        :placement="['publishing', 'page:publishing']"
        :validator="validator"
        validator-prefix="customFields"
        :disabledFields="$path('metadata.disabledFields.page', item)"
      />

      <div v-if="isActive" class="page-mutate-presentation__cutoff row between">
        <CustomFieldSchemas
          @add="addCustomField"
          :include="customFieldInclusion"
          :exclude="value.customFieldsConfig"
          class="mt-4"
        />
      </div>
    </template>
  </Card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Card from '@/components/Card'

import { InputComponent } from 'vue-elder-input'
import { Paywall } from '@/components/Upsell'
import { DateTransformer, UrlTransformer } from '@/utils/transformers'
import { i18n } from '@/i18n'
import { assocPath } from 'ramda'

import CustomFields from '@kvass/custom-fields'
import CustomFieldSchemas from '@/components/Project/CustomFieldSchemas'
import RichTextComponent from '@kvass/rich-text'
import InputDate from '@/components/InputDate'
import DNS from '@/components/DNS/Component.vue'

const meta = {
  order: 1002,
  id: 'publishing',
  label: i18n.tc('publishing'),
  icon: ['fal', 'globe'],
  fields: ['publishing.domain'],
}

export { meta }
export default {
  props: {
    value: Object,
    labels: Object,
    isActive: Boolean,
    validator: Object,
    customFieldSchemas: { type: Array, default: () => [] },
  },
  computed: {
    ...mapState('Page', {
      item(state) {
        return state.item
      },
    }),
    ...mapGetters('Page', ['getDisabledField']),
    from: DateTransformer('value.publishing.from'),
    to: DateTransformer('value.publishing.to'),
    domain: UrlTransformer('value.publishing.domain'),
    customFieldInclusion() {
      return [{ source: 'seo.json' }, { source: 'privacy-policy.json' }]
    },
    showDns() {
      const hasDomain = Boolean(this.$path('publishing.domain', this.value))
      const hasValidDomain = this.validator.fields['publishing.domain'].valid
      return hasDomain && hasValidDomain
    },
    domainProps() {
      let dnsStatus = this.$path('dns.status', this.$refs)
      if (this.validator.fields['publishing.domain'].valid === false)
        return {
          'is-valid': false,
          'validation-message': this.validator.fields['publishing.domain'].error,
        }
      if (!dnsStatus) return { 'is-valid': undefined }

      return { 'is-valid': true }
    },
  },
  methods: {
    addCustomField(item) {
      this.value.customFieldsConfig.push({
        source: item.id,
        placement: 'publishing',
        schema: item.fields,
      })
    },
    removeCustomField(item) {
      this.value.customFields = assocPath(item.key.split('.'), null, this.value.customFields)
      this.value.customFieldsConfig = this.value.customFieldsConfig.filter(c => c.source !== item.source)
    },
  },
  components: {
    Card,
    InputComponent,
    RichTextComponent,
    DNS,
    CustomFields,
    Paywall,
    InputDate,
    CustomFieldSchemas,
  },
}
</script>

<style lang="scss">
.page-mutate-publishing {
  $cols: 2;
  max-width: 800px;

  &__dns {
    background-color: css-alpha('primary', 0.05);
    border-radius: var(--border-radius);
    padding: 1rem;

    & + & {
      margin-top: 1rem;
    }
  }

  &__input {
    display: grid;
    grid-template-columns: repeat($cols, 1fr);
    grid-gap: 1rem 2rem;
  }

  [data-field='domain'] {
    grid-column-end: span $cols;
  }
}
</style>
